// Mobile Mail — orchestrator.
// Mobile Mail v1.2 (2026-09-14):
//   - State lifted to Page: items / page / hasMore / isRefreshing / isLoadingMore
//   - useMailMessages removed from List; Page calls mailV2Api.listMessages directly
//   - 3 handlers: handleRefreshClick (syncJobs.refresh + short poll + list refetch),
//     handleLoadMore (append), handleSelectAccount (switch + reset)
//   - IntersectionObserver on bottom sentinel triggers handleLoadMore
//   - Drawer receives accounts + currentAccountId + onSelectAccount
//   - syncUiMode "idle" | "refreshing" — poll-based (Plan C)
// No router sub-routes, no global store. Reuses useMailAccounts.

import { useCallback, useEffect, useRef, useState } from "react";
import { useMailAccounts } from "../../modules/mail-v2/hooks/useMailAccounts";
import { mapFolderIdToMailboxName, mapMessage } from "../../modules/mail-v2/api/mappers";
import type { MessageV2View } from "../../modules/mail-v2/api/mappers";
import { mailV2Api } from "../../api/mail-v2";
import { refreshMailIfStale } from "../../modules/mail-v2/services/mailRefreshManager";
import { MobileMailList } from "../mail/MobileMailList";
import { MobileMailDetail } from "../mail/MobileMailDetail";
import type { ReplyInit } from "../mail/MobileMailDetail";
import { MobileMailCompose } from "../mail/MobileMailCompose";
import {
  MobileFolderDrawer,
  FOLDERS,
  type FolderId,
} from "../mail/MobileFolderDrawer";

type View = "list" | "detail" | "compose";

const PAGE_SIZE = 50;

const FOLDER_LABEL: Record<FolderId, string> = {
  inbox: "受信トレイ",
  sent: "送信済み",
  drafts: "下書き",
  all: "すべてのメール",
  trash: "ゴミ箱",
  spam: "迷惑メール",
};

const EMPTY_TEXT: Record<FolderId, string> = {
  inbox: "受信メールはありません",
  sent: "送信済みメールはありません",
  drafts: "下書きはありません",
  all: "メールはありません",
  trash: "ゴミ箱は空です",
  spam: "迷惑メールはありません",
};

export const MobileMailPage = () => {
  const [view, setView] = useState<View>("list");
  const [folderId, setFolderId] = useState<FolderId>("inbox");
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [replyInit, setReplyInit] = useState<ReplyInit | null>(null);
  const [isFolderMenuOpen, setIsFolderMenuOpen] = useState(false);

  // v1.2: account selection
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  // v1.2: pagination / refresh state
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<MessageV2View[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [refreshSeq, setRefreshSeq] = useState(0);

  const { accounts } = useMailAccounts();

  // 解决 AccountV2View mapper 不含 is_active 字段的问题 (PC 稳定,不动 mapper)。
  // 优先用用户已选的账号,否则取第一个。
  const accountId =
    selectedAccountId && accounts.some((a) => a.id === selectedAccountId)
      ? selectedAccountId
      : accounts[0]?.id ?? "";

  // v1.2: 监听 accountId / folderId 变化,自动重新拉第 1 页。
  useEffect(() => {
    if (!accountId) {
      setItems([]);
      setHasMore(false);
      setListLoading(false);
      return;
    }
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    (async () => {
      try {
        const res = await mailV2Api.listMessages({
          mail_account_id: accountId,
          mailbox: mapFolderIdToMailboxName(folderId),
          page: 1,
          page_size: PAGE_SIZE,
        });
        if (cancelled) return;
        setItems((res.items ?? []).map(mapMessage));
        setHasMore((res.items ?? []).length >= PAGE_SIZE);
        setPage(1);
      } catch {
        if (!cancelled) setListError("メールの取得に失敗しました");
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, folderId, refreshSeq]);

  useEffect(() => {
    if (!accountId) return;
    void refreshMailIfStale(accountId, { source: "mobile" }).then((result) => {
      if (result.status === "success") setRefreshSeq((value) => value + 1);
    });
  }, [accountId]);

  // v1.2: 刷新按钮 (syncJobs.refresh + poll + listMessages)
  const handleRefreshClick = useCallback(async () => {
    if (isRefreshing || !accountId) return;
    setIsRefreshing(true);
    setListError(null);
    try {
      await refreshMailIfStale(accountId, { force: true, source: "mobile_manual" });
      const list = await mailV2Api.listMessages({
        mail_account_id: accountId,
        mailbox: mapFolderIdToMailboxName(folderId),
        page: 1,
        page_size: PAGE_SIZE,
      });
      setItems((list.items ?? []).map(mapMessage));
      setHasMore((list.items ?? []).length >= PAGE_SIZE);
      setPage(1);
    } catch {
      setListError("更新に失敗しました");
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, accountId, folderId]);

  // v1.2: 加载更多 (append)
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || isRefreshing || !hasMore || !accountId) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    try {
      const res = await mailV2Api.listMessages({
        mail_account_id: accountId,
        mailbox: mapFolderIdToMailboxName(folderId),
        page: nextPage,
        page_size: PAGE_SIZE,
      });
      const mapped = (res.items ?? []).map(mapMessage);
      setItems((prev) => [...prev, ...mapped]);
      setHasMore((res.items ?? []).length >= PAGE_SIZE);
      setPage(nextPage);
    } catch {
      setListError("追加読み込みに失敗しました");
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, isRefreshing, hasMore, accountId, folderId, page]);

  // v1.2: IntersectionObserver 触发 loadMore
  const loadMoreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (view !== "list") return;
    const node = loadMoreRef.current;
    if (!node) return;
    if (!hasMore || isLoadingMore || isRefreshing) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void handleLoadMore();
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [view, hasMore, isLoadingMore, isRefreshing, handleLoadMore]);

  // v1.2: 切换邮箱账号
  const handleSelectAccount = useCallback((accId: string) => {
    setSelectedAccountId(accId);
    setFolderId("inbox");
    setItems([]);
    setPage(1);
    setHasMore(true);
    setListError(null);
    setIsFolderMenuOpen(false);
  }, []);

  // 切 folder 时重置分页
  const handleSelectFolder = useCallback((id: FolderId) => {
    if (id === folderId) {
      setIsFolderMenuOpen(false);
      return;
    }
    setFolderId(id);
    setItems([]);
    setPage(1);
    setHasMore(true);
    setListError(null);
    setIsFolderMenuOpen(false);
  }, [folderId]);

  // 现有 detail / compose handler (保留)
  const handleSelectMessage = (msg: MessageV2View) => {
    setSelectedMessageId(msg.id);
    setView("detail");
  };

  const handleDetailBack = () => {
    setSelectedMessageId(null);
    // 返回 list 后刷新列表 (mark-read 反映)
    setRefreshSeq((k) => k + 1);
    setView("list");
  };

  const handleDetailReply = (init: ReplyInit) => {
    setReplyInit(init);
    setView("compose");
  };

  const handleComposeCancel = () => {
    setReplyInit(null);
    setView(selectedMessageId ? "detail" : "list");
  };

  const handleComposeSent = () => {
    setReplyInit(null);
    setSelectedMessageId(null);
    setRefreshSeq((k) => k + 1);
    setView("list");
  };

  if (view === "detail" && selectedMessageId) {
    return (
      <MobileMailDetail
        key={selectedMessageId}
        messageId={selectedMessageId}
        onBack={handleDetailBack}
        onReply={handleDetailReply}
      />
    );
  }

  if (view === "compose") {
    return (
      <MobileMailCompose
        accountId={accountId}
        mode={replyInit ? "reply" : "new"}
        replyInit={replyInit}
        onCancel={handleComposeCancel}
        onSent={handleComposeSent}
      />
    );
  }

  return (
    <>
      <MobileMailList
        items={items}
        loading={listLoading}
        error={listError}
        loadingMore={isLoadingMore}
        hasMore={hasMore}
        isRefreshing={isRefreshing}
        accountId={accountId}
        folderLabel={FOLDER_LABEL[folderId]}
        emptyText={EMPTY_TEXT[folderId]}
        onSelectMessage={handleSelectMessage}
        onCompose={() => {
          setReplyInit(null);
          setView("compose");
        }}
        onRefresh={handleRefreshClick}
        onOpenFolderMenu={() => setIsFolderMenuOpen(true)}
        loadMoreRef={loadMoreRef}
      />
      {isFolderMenuOpen && (
        <MobileFolderDrawer
          currentFolderId={folderId}
          onSelect={handleSelectFolder}
          onClose={() => setIsFolderMenuOpen(false)}
          accounts={accounts}
          currentAccountId={accountId || null}
          onSelectAccount={handleSelectAccount}
        />
      )}
    </>
  );
};
